import React, { useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import strings from 'utils/strings'
import { COMPONENT, DASH_VIEW } from 'utils/constants'
import { isEmpty, inc, propOr, prop, values } from 'ramda'
import { useSelector } from 'hooks/redux'
import { DropLocation } from 'components/DragAndDrop'
import { Button, Paragraph } from 'components/patterns'

import DraggablePart from 'components/SwitchAndGauge/DashDrawer/DraggableList/DraggablePart'
import {
  getStagedComponents,
  getStagedLocations,
  getUnassignedComponents,
} from 'selectors/dash'

import {
  DraggableUnassignedPartList,
  UnAssignedPartHeader,
  UnAssignedPartHeaderIcon,
} from 'components/SwitchAndGauge/DashDrawer/DraggableList/DraggablePartListView.style'
import setTheme from 'theme'

import {
  getPanelName,
  generateStagedComponentLetter,
  getComponentName,
  getComponentDescription,
} from 'components/SwitchAndGauge/utils'

import { useDispatch } from 'react-redux'
import { setSelectedIdWithDrawerAndMeta } from 'actions/navigator'
import {
  PartDescription,
  MsfHeader,
  ComponentList,
  StyledHoverableInfoIcon,
  SecondaryHeaderContainer,
  StagingList,
  SubHeaderContainer,
  PanelName,
  DragIcon,
} from './PreProdUnassignedComponents.style'

const PreProdUnassignedComponents = () => {
  const themes = setTheme()
  const stagedComponents = useSelector(getStagedComponents)
  const stagedLocations = useSelector(getStagedLocations)
  const unassignedComponents = useSelector(getUnassignedComponents)

  const dispatch = useDispatch()

  const handleOnClick = useCallback(
    (location) => {
      dispatch(
        setSelectedIdWithDrawerAndMeta(DASH_VIEW, {
          view: COMPONENT,
          id: location,
        })
      )
    },
    [dispatch]
  )

  const renderMsfChildren = useCallback(
    (childrenLocations) =>
      childrenLocations.map((location, index) => {
        const component = propOr({}, location, stagedComponents)
        const { panelNumber, panelDescription } = component

        if (!panelDescription) {
          return (
            <div key={location} style={{ paddingLeft: 22 }}>
              {strings.empty}
            </div>
          )
        }

        return (
          <DropLocation key={location} location={location}>
            <DraggablePart location={location} index={inc(index)}>
              <PartDescription title={panelNumber}>
                <Button link onClick={() => handleOnClick(location)}>
                  {panelDescription}
                </Button>
              </PartDescription>
            </DraggablePart>
          </DropLocation>
        )
      }),
    [handleOnClick, stagedComponents]
  )

  const list = useMemo(
    () =>
      stagedLocations.map((locationDetails, index) => {
        const { hasChildren, location, children, panel, stagedCount } =
          locationDetails
        const component = propOr({}, location, stagedComponents)
        const componentName = getComponentName(component)
        const description = getComponentDescription(component)

        if (hasChildren) {
          return (
            <ComponentList key={location} index={index}>
              <DropLocation key={location} location={location}>
                <DraggablePart location={location} index={inc(index)}>
                  <MsfHeader key={`msf_${location}`}>
                    {strings.msf} {generateStagedComponentLetter(stagedCount)}
                  </MsfHeader>
                  <PanelName>({getPanelName(panel)})</PanelName>
                </DraggablePart>
                {renderMsfChildren(children)}
              </DropLocation>
            </ComponentList>
          )
        }

        return (
          <ComponentList key={location} index={index}>
            <DropLocation key={location} location={location}>
              <DraggablePart location={location} index={inc(index)}>
                <PartDescription title={componentName}>
                  <Button link onClick={() => handleOnClick(location)}>
                    {description}
                  </Button>
                </PartDescription>
              </DraggablePart>
            </DropLocation>
          </ComponentList>
        )
      }),
    [handleOnClick, renderMsfChildren, stagedComponents, stagedLocations]
  )

  const unassignedComponentsList = useMemo(
    () =>
      values(unassignedComponents).map((component, index) => {
        const location = prop('location', component)
        return (
          <DropLocation key={location} location={location}>
            <DraggablePart location={location} index={inc(index)} />
          </DropLocation>
        )
      }),
    [unassignedComponents]
  )

  if (isEmpty(stagedLocations) && isEmpty(unassignedComponents))
    return (
      <DraggableUnassignedPartList>
        <SubHeaderContainer>
          <UnAssignedPartHeader disabled>
            {strings.parts_without_locations}
          </UnAssignedPartHeader>
          <DragIcon>
            <StyledHoverableInfoIcon
              tooltipPosition="bottomLeft"
              tooltip={strings.part_without_location_info_tooltip}
            />
          </DragIcon>
        </SubHeaderContainer>
      </DraggableUnassignedPartList>
    )

  return (
    <DraggableUnassignedPartList>
      <>
        <UnAssignedPartHeader>
          {strings.parts_without_locations}
        </UnAssignedPartHeader>
        <DragIcon>
          <StyledHoverableInfoIcon
            tooltipPosition="bottomLeft"
            tooltip={strings.part_without_location_info_tooltip}
          />
        </DragIcon>
      </>
      {!isEmpty(stagedLocations) && (
        <StagingList>
          <SecondaryHeaderContainer>
            <UnAssignedPartHeaderIcon
              width={22}
              height={18}
              icon="alert_exclamation_triangle_solid"
              fill={themes.badgeWarningColor}
            />
            <div>
              <Paragraph weight="bold">{strings.staging_area}</Paragraph>
              <SubHeaderContainer>
                <Paragraph size="small">
                  {strings.these_parts_require_placement}
                </Paragraph>
                <DragIcon>
                  <StyledHoverableInfoIcon
                    tooltipPosition="top"
                    tooltip={strings.staging_location_info_message}
                  />
                </DragIcon>
              </SubHeaderContainer>
            </div>
          </SecondaryHeaderContainer>
          {list}
        </StagingList>
      )}
      {!isEmpty(unassignedComponents) && (
        <StagingList>
          <SecondaryHeaderContainer>
            <StyledIcon />
            <SubHeaderContainer>
              <Paragraph weight="bold">{strings.component_errors}</Paragraph>
              <DragIcon>
                <StyledHoverableInfoIcon
                  tooltip={strings.parts_with_errors_message}
                />
              </DragIcon>
            </SubHeaderContainer>
          </SecondaryHeaderContainer>
          {unassignedComponentsList}
        </StagingList>
      )}
    </DraggableUnassignedPartList>
  )
}

PreProdUnassignedComponents.propTypes = {
  components: PropTypes.object,
}

export default PreProdUnassignedComponents

const SvgComponent = (props) => (
  <svg
    width={20}
    height={20}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M19.122 7.88 12.121.877a3.001 3.001 0 0 0-4.242 0L.878 7.879a3 3 0 0 0 0 4.242l7.001 7.001a3 3 0 0 0 4.242 0l7.001-7.001a3.001 3.001 0 0 0 0-4.242zm-5.166 5.148a.975.975 0 0 0-.287-.69l-2.281-2.345 2.33-2.324a.975.975 0 0 0-.692-1.667.98.98 0 0 0-.692.286l-2.32 2.324-2.33-2.324a.99.99 0 0 0-1.682.715c.003.262.11.512.297.695l2.33 2.325-2.33 2.314a.975.975 0 0 0 .693 1.667.98.98 0 0 0 .692-.286l2.28-2.324 2.32 2.324a.98.98 0 0 0 1.672-.69z"
      fill="#B4040C"
    />
  </svg>
)

const StyledIcon = styled(SvgComponent)`
  display: inline;
  margin-right: 8px;
`
